<template>
  <div class="command-expiry">
    <div class="command-expiry__label">Expiry</div>

    <v-radio-group
      :value="value.mode"
      row
      dense
      hide-details
      class="mt-0 pt-0"
      :disabled="disabled"
      @change="setMode($event)"
    >
      <v-radio :label="defaultLabel" value="default" />
      <v-radio label="Expire after" value="ttl" />
      <v-radio label="Expire at" value="absolute" />
    </v-radio-group>

    <!-- Relative: sent as ttlMinutes -->
    <v-row v-if="value.mode === 'ttl'" dense class="mt-1">
      <v-col cols="6" sm="3">
        <v-text-field
          :value="amount"
          type="number"
          min="1"
          label="Amount"
          hide-details
          dense
          :disabled="disabled"
          @input="setAmount($event)"
        />
      </v-col>
      <v-col cols="6" sm="3">
        <v-select
          :value="unit"
          :items="unitOptions"
          label="Unit"
          hide-details
          dense
          :disabled="disabled"
          @change="setUnit($event)"
        />
      </v-col>
      <v-col cols="12" sm="6" class="d-flex align-center">
        <span class="command-expiry__hint">
          {{ ttlSummary }}
        </span>
      </v-col>
    </v-row>

    <!-- Absolute: sent as expiresAt, wins over ttlMinutes server-side -->
    <v-row v-else-if="value.mode === 'absolute'" dense class="mt-1">
      <v-col cols="12" sm="4">
        <v-menu
          v-model="dateMenu"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="date"
              label="Date"
              readonly
              hide-details
              dense
              :disabled="disabled"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            :value="date"
            :min="today"
            @input="setDate($event)"
          />
        </v-menu>
      </v-col>
      <v-col cols="12" sm="3">
        <v-menu
          v-model="timeMenu"
          :close-on-content-click="false"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="time"
              label="Time"
              readonly
              hide-details
              dense
              :disabled="disabled"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-time-picker
            :value="time"
            format="24hr"
            @input="setTime($event)"
          />
        </v-menu>
      </v-col>
      <v-col cols="12" sm="5" class="d-flex align-center">
        <span class="command-expiry__hint">{{ absoluteSummary }}</span>
      </v-col>
    </v-row>
  </div>
</template>

<script>
  import { describeDefaultExpiry, formatMinutes } from '@/util/commandCatalog'

  const UNIT_MINUTES = { minutes: 1, hours: 60, days: 1440 }

  function pad (value) {
    return String(value).padStart(2, '0')
  }

  export default {
    name: 'CommandExpiryPicker',

    props: {
      // { mode: 'default' | 'ttl' | 'absolute', ttlMinutes, expiresAt }
      value: {
        type: Object,
        default: () => ({ mode: 'default', ttlMinutes: null, expiresAt: null }),
      },
      // Catalog entry: { defaultTtlMinutes, neverExpiresByDefault }
      defaultInfo: {
        type: Object,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
    },

    data () {
      return {
        amount: 30,
        unit: 'minutes',
        date: '',
        time: '12:00',
        dateMenu: false,
        timeMenu: false,
      }
    },

    computed: {
      defaultLabel () {
        return describeDefaultExpiry(this.defaultInfo)
      },

      unitOptions () {
        return [
          { text: 'Minutes', value: 'minutes' },
          { text: 'Hours', value: 'hours' },
          { text: 'Days', value: 'days' },
        ]
      },

      today () {
        return new Date().toISOString().slice(0, 10)
      },

      totalMinutes () {
        const amount = Number(this.amount)
        if (!Number.isFinite(amount) || amount <= 0) {
          return 0
        }
        return Math.round(amount * UNIT_MINUTES[this.unit])
      },

      ttlSummary () {
        if (!this.totalMinutes) {
          return 'Enter a positive amount'
        }
        const at = new Date(Date.now() + this.totalMinutes * 60000)
        return `Expires ${formatMinutes(this.totalMinutes)} after sending (about ${at.toLocaleString()})`
      },

      absoluteSummary () {
        if (!this.date) {
          return 'Pick a date'
        }
        const at = this.localDateTime()
        if (!at) {
          return 'Pick a date'
        }
        return at.getTime() <= Date.now()
          ? 'That time is in the past'
          : `Expires ${at.toLocaleString()}`
      },
    },

    watch: {
      // Seed the absolute pickers with a sensible near-future default.
      'value.mode' (mode) {
        if (mode === 'absolute' && !this.date) {
          const soon = new Date(Date.now() + 60 * 60000)
          this.date = `${soon.getFullYear()}-${pad(soon.getMonth() + 1)}-${pad(soon.getDate())}`
          this.time = `${pad(soon.getHours())}:${pad(soon.getMinutes())}`
          this.emitAbsolute()
        }
      },
    },

    methods: {
      localDateTime () {
        if (!this.date) {
          return null
        }
        const [hours, minutes] = String(this.time || '00:00').split(':')
        const [year, month, day] = this.date.split('-').map(Number)
        const at = new Date(year, month - 1, day, Number(hours) || 0, Number(minutes) || 0, 0, 0)
        return Number.isNaN(at.getTime()) ? null : at
      },

      emit (next) {
        this.$emit('input', { ...this.value, ...next })
      },

      setMode (mode) {
        if (mode === 'ttl') {
          this.emit({ mode, ttlMinutes: this.totalMinutes, expiresAt: null })
        } else if (mode === 'absolute') {
          this.emit({ mode, ttlMinutes: null, expiresAt: this.absoluteIso() })
        } else {
          this.emit({ mode: 'default', ttlMinutes: null, expiresAt: null })
        }
      },

      setAmount (raw) {
        this.amount = raw
        this.emit({ ttlMinutes: this.totalMinutes })
      },

      setUnit (unit) {
        this.unit = unit
        this.emit({ ttlMinutes: this.totalMinutes })
      },

      setDate (date) {
        this.date = date
        this.dateMenu = false
        this.emitAbsolute()
      },

      setTime (time) {
        this.time = time
        this.timeMenu = false
        this.emitAbsolute()
      },

      absoluteIso () {
        const at = this.localDateTime()
        // Local wall-clock in, UTC on the wire.
        return at ? at.toISOString() : null
      },

      emitAbsolute () {
        this.emit({ expiresAt: this.absoluteIso() })
      },
    },
  }
</script>

<style scoped>
.command-expiry {
  margin-top: 4px;
}

.command-expiry__label {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.command-expiry__hint {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}
</style>
